import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormInput from '../FormInput';
import directDebitLogo from '../../../images/directdebit.gif';
import Checkbox from '../checkbox/checkbox';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BankDetailsCommon from './BankDetailsCommon';
import { useBankDetailsForm } from './useBankDetailsForm';
import { AccountType } from 'Constants/Common';
import { useLocation } from 'react-router-dom';

export type BankDetailErrors = {
    accountHolderName?: string;
    sortCode?: string;
    accountNumber?: string;
    bankValidation?: string;
    paymentDay?: string;
    directDebitPolicy?: string;
};

const AmendBankDetailsPage = (): JSX.Element => {
    const { t } = useTranslation();
    const location = useLocation();
    const [expanded, setExpanded] = useState(true);
    const businessPartnerId = (location.state as { businessPartnerId: number })
        ?.businessPartnerId;
    const {
        scrollDivRef,
        bankDetails,
        bankDetailErrors,
        daysOptions,
        onChange,
        onSortCodeChange,
        onPaymentDayChange,
        getErrors,
        onSubmit,
        agreedDirectDebit,
        setAgreedDirectDebit,
        agreedDirectDebitDueDate,
        setAgreedDirectDebitDueDate,
        onBack
    } = useBankDetailsForm({
        onSuccess: () => {
            toast.success(t('direct_debit_guarantee.update-bank-success'), {
                onClose: () => {
                    onBack();
                }
            });
        },
        t,
        requireAgreements: true,
        showPaymentDay: false,
        accountType: AccountType.Collections,
        referenceNumber: '',
        businessPartnerId: businessPartnerId
    });
    return (
        <div ref={scrollDivRef}>
            <BankDetailsCommon
                title={t('amend_bank_details_title')}
                instruction={t('amend_bank_details_instruction')}
                bankDetails={bankDetails}
                bankDetailErrors={bankDetailErrors}
                daysOptions={daysOptions}
                t={t}
                onChange={onChange}
                onSortCodeChange={onSortCodeChange}
                onPaymentDayChange={onPaymentDayChange}
                onSubmit={onSubmit}
                onBack={onBack}
                getErrors={getErrors}
                showPaymentDay={false}
                showBankInputSection={true}
                accountType={AccountType.Collections}>
                <Accordion
                    className='mb-0 mt-0 header-background'
                    expanded={expanded}
                    onChange={() => setExpanded(!expanded)}>
                    <AccordionSummary
                        className='accordion-header'
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls='vehiclePaneld-content'
                        id='vehiclePaneld-header'>
                        <p className='p1'>
                            {t('direct_debit_guarantee_title')}
                        </p>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className='d-flex'>
                            <p className='flex-grow-1 flex-shrink-1 fw-bold'>
                                {t('the_direct_debit_guarantee_title')}
                            </p>
                            <div>
                                <img
                                    src={directDebitLogo}
                                    alt='Direct Debit Logo'
                                />
                            </div>
                        </div>
                        <ul>
                            <li>{t('direct_debit_guarantee.text_1')}</li>
                            <li>{t('direct_debit_guarantee.text_2')}</li>
                            <li>
                                {t('direct_debit_guarantee.text_3')}
                                <br />* {t('direct_debit_guarantee.text_4')}
                            </li>
                            <li>{t('direct_debit_guarantee.text_5')}</li>
                        </ul>
                    </AccordionDetails>
                </Accordion>
                <FormInput
                    id='direct-debit-agreement'
                    errorText={bankDetailErrors.directDebitPolicy}>
                    <div className='d-flex flex-column gap-3'>
                        <Checkbox
                            id='direct-debit-agreement1'
                            checked={agreedDirectDebit}
                            text={t('direct-debit-agreement1')}
                            onChange={() =>
                                setAgreedDirectDebit(!agreedDirectDebit)
                            }
                        />
                        <Checkbox
                            id='direct-debit-agreement2'
                            checked={agreedDirectDebitDueDate}
                            text={t('direct-debit-agreement2')}
                            onChange={() =>
                                setAgreedDirectDebitDueDate(
                                    !agreedDirectDebitDueDate
                                )
                            }
                        />
                    </div>
                </FormInput>
            </BankDetailsCommon>
        </div>
    );
};

export default AmendBankDetailsPage;
